import { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../../../context/AuthContext';

const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'https://nexploy-eg.com';

const useProfilePics = ({
  token,
  conversations,
  employeeProfilePics,
  setEmployeeProfilePics,
}) => {
  const picCacheRef = useRef(new Map());
  const [loadingPics, setLoadingPics] = useState(new Set());

  const fetchEmployeeProfilePic = async (infinityCode) => {
    try {
      const isGroup = String(infinityCode).startsWith('group-');
      const actualId = isGroup ? String(infinityCode).replace('group-', '') : infinityCode;

      if (employeeProfilePics[infinityCode]) {
        return employeeProfilePics[infinityCode];
      }

      if (picCacheRef.current.has(infinityCode)) {
        const cachedUrl = picCacheRef.current.get(infinityCode);
        setEmployeeProfilePics((prev) => ({
          ...prev,
          [infinityCode]: cachedUrl,
        }));
        return cachedUrl;
      }

      if (loadingPics.has(infinityCode)) {
        return null;
      }

      setLoadingPics((prev) => new Set([...prev, infinityCode]));

      const fetchUrl = isGroup
        ? `${API_BASE_URL}/api/chat-groups/${actualId}/photo`
        : `${API_BASE_URL}/employees/pic/${infinityCode}`;

      const response = await apiFetch(fetchUrl, {
        method: 'GET',
      });

      if (response.ok) {
        const blob = await response.blob();
        if (blob && blob.size > 0) {
          const url = URL.createObjectURL(blob);
          picCacheRef.current.set(infinityCode, url);
          setEmployeeProfilePics((prev) => ({
            ...prev,
            [infinityCode]: url,
          }));
          setLoadingPics((prev) => {
            const newSet = new Set(prev);
            newSet.delete(infinityCode);
            return newSet;
          });
          return url;
        }
      }

      setLoadingPics((prev) => {
        const newSet = new Set(prev);
        newSet.delete(infinityCode);
        return newSet;
      });

      return null;
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error(`❌ Error loading pic for ${infinityCode}:`, err.message);
      }

      setLoadingPics((prev) => {
        const newSet = new Set(prev);
        newSet.delete(infinityCode);
        return newSet;
      });

      return null;
    }
  };

  useEffect(() => {
    const fetchAllProfilePics = async () => {
      if (!token || conversations.length === 0) return;

      const missingPics = conversations
        .filter((conv) => !employeeProfilePics[conv.id])
        .map((conv) => conv.id);

      if (missingPics.length === 0) return;

      const BATCH_SIZE = 5;

      for (let i = 0; i < missingPics.length; i += BATCH_SIZE) {
        const batch = missingPics.slice(i, i + BATCH_SIZE);

        await Promise.all(batch.map((id) => fetchEmployeeProfilePic(id)));

        if (i + BATCH_SIZE < missingPics.length) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }
    };

    fetchAllProfilePics();
  }, [conversations, token]);

  useEffect(() => {
    // Intentionally no longer revoking Blob URLs on unmount. 
    // They are stored in a global context (employeeProfilePics) and needed by 
    // the always-mounted Mini Chat Dock even after the MessengerPage unmounts.
    // They will be garbage collected when the user logs out or the page reloads.
  }, []);

  return { employeeProfilePics, fetchEmployeeProfilePic, loadingPics, picCacheRef };
};

export default useProfilePics;